import * as ss from "superstruct";

interface CustomField {
  name: string;
  value: string;
}

type SchemasLike = { [key: string]: ss.Struct<any, any> };

export type ParsedCustomFieldsGroup<Schemas extends SchemasLike> = {
  [K in keyof Schemas]: { kind: K; data: ss.Infer<Schemas[K]>[] };
}[keyof Schemas];

export type ParsedCustomField<Schemas extends SchemasLike> = {
  [K in keyof Schemas]: { kind: K; data: ss.Infer<Schemas[K]> };
}[keyof Schemas];

export function parseCustomFieldsGroup<Schemas extends SchemasLike>(
  schemas: Schemas,
  customFields: CustomField[] | undefined
): ParsedCustomFieldsGroup<Schemas> | undefined {
  if (!customFields || customFields.length === 0) return undefined;
  const kind = customFields[0].name;
  const data: any[] = [];
  const s = schemas[kind];
  if (!s) return undefined;
  for (const f of customFields) {
    let json: any = undefined;
    try {
      json = JSON.parse(f.value);
    } catch {
      // do nothing
    }
    const r = ss.validate(json, s);
    if (!r[0]) {
      data.push(r[1]);
    }
  }
  return { kind, data };
}

export function parseCustomFields<Schemas extends SchemasLike>(
  schemas: Schemas,
  customFields: CustomField[] | undefined
): ParsedCustomField<Schemas>[] {
  if (!customFields || customFields.length === 0) return [];
  const out: { kind: string; data: any }[] = [];
  for (const f of customFields) {
    const kind = f.name;
    const s = schemas[kind];
    if (!s) continue;
    let json: any = undefined;
    try {
      json = JSON.parse(f.value);
    } catch {
      // do nothing
    }
    const r = ss.validate(json, s);
    if (!r[0]) {
      out.push({ kind, data: r[1] });
    }
  }
  return out;
}

export function extractCustomFields<Schemas extends SchemasLike, Name extends keyof Schemas>(
  schemas: Schemas,
  name: Name,
  customFields: CustomField[] | undefined
): ss.Infer<Schemas[Name]>[] {
  if (!customFields) return [];
  const out: Schemas[Name][] = [];
  const s = schemas[name];
  for (const f of customFields) {
    let json: any = undefined;
    try {
      json = JSON.parse(f.value);
    } catch {
      // do nothing
    }
    const r = ss.validate(json, s);
    if (!r[0]) {
      out.push(r[1] as any);
    }
  }
  return out;
}

export function extractCustomField<Schemas extends SchemasLike, Name extends keyof Schemas>(
  schemas: Schemas,
  name: Name,
  customFields: CustomField[] | undefined
): ss.Infer<Schemas[Name]> | undefined {
  const v = extractCustomFields(schemas, name, customFields);
  return v.length > 0 ? v[0] : undefined;
}
